import React, { useState, useEffect } from 'react';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { titleCase } from '../../../helpers/UtilityHelper';
import { getRole } from '../../../helpers/ReduxHelpers';
import { Button, FormControlLabel, Switch, Typography, Box, List, ListItem, ListItemText, Chip, Grid, IconButton, Menu, MenuItem, Divider, ListItemIcon, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MUIModal from '../../../common/MUIModal';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFTextField } from '../../../common/hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { postGeneric } from '../../../common/constants';
import { apiConstants } from '../../../common/constants';
import { RHFCheckbox } from '../../../common/hook-form/RHFCheckbox';
import { filterBarSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { primaryTextSx, secondaryTextSx, cellStackSx } from '../../orders/list2/cellStyles';

// An "API only" user is a ROLE_ADMIN without MERCHANT_ADMIN module access —
// they can call APIs but have no panel UI. None of the per-row actions
// (edit modules, PII / MFA / address toggles, mobile edit) apply to them.
const isApiOnlyUser = (user) =>
    (user?.roles || []).includes('ROLE_ADMIN') &&
    !(user?.accessList || []).includes('MERCHANT_ADMIN');

// Per-row actions consolidated into a single kebab menu so the Action column
// is no longer a stack of 5 buttons.
const RowActionsMenu = ({ row, onEditClick, onOpenOtpDialog }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const piiAccess = row?.piiAccess === true || row?.piiAccess === 'true';
    const mfaenabled = row?.mfaenabled === true || row?.mfaenabled === 'true';
    const unmaskedAddressAccess = row?.unmaskedAddressAccess === true || row?.unmaskedAddressAccess === 'true';
    const apiOnly = isApiOnlyUser(row);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const run = (fn) => () => { handleClose(); fn(); };

    if (apiOnly) {
        return (
            <Tooltip title="API-only user — no panel actions apply" arrow>
                <span>
                    <IconButton size="small" disabled aria-label="user actions (disabled for API-only user)">
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <>
            <IconButton size="small" onClick={handleOpen} aria-label="user actions">
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 220 } }}
            >
                <MenuItem onClick={run(() => onEditClick(row.id))}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    Edit Roles
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={run(() => onOpenOtpDialog(row, 'piiAccess'))}>
                    <ListItemIcon>
                        <SecurityIcon fontSize="small" color={piiAccess ? 'error' : 'inherit'} />
                    </ListItemIcon>
                    {piiAccess ? 'Remove PII Access' : 'Allow PII Access'}
                </MenuItem>
                <MenuItem onClick={run(() => onOpenOtpDialog(row, 'mfa'))}>
                    <ListItemIcon>
                        <VerifiedUserIcon fontSize="small" color={mfaenabled ? 'error' : 'inherit'} />
                    </ListItemIcon>
                    {mfaenabled ? 'Disable MFA' : 'Enable MFA'}
                </MenuItem>
                <MenuItem onClick={run(() => onOpenOtpDialog(row, 'unmaskedAddressAccess'))}>
                    <ListItemIcon>
                        <LocationOnIcon fontSize="small" color={unmaskedAddressAccess ? 'error' : 'inherit'} />
                    </ListItemIcon>
                    {unmaskedAddressAccess ? 'Remove Address View' : 'Allow Address View'}
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={run(() => onOpenOtpDialog(row, 'mobileNumber'))}>
                    <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                    Edit Mobile Number
                </MenuItem>
            </Menu>
        </>
    );
};

const UserList = (props) => {
    const [sizePerPage, setSizePerPage] = useState(20)
    const [page, setPage] = useState(0)
    const [loginModalOpen, setLoginModalOpen] = useState(false)
    const [selectedUserLogins, setSelectedUserLogins] = useState(null)
    const [otpDialogOpen, setOtpDialogOpen] = useState(false)
    const [otpSent, setOtpSent] = useState(false)
    const [sendingOtp, setSendingOtp] = useState(false)
    const [verifyingOtp, setVerifyingOtp] = useState(false)
    const [sendOtpData, setSendOtpData] = useState(null);
    const [tokenId, setTokenId] = useState(null);
    const { enqueueSnackbar } = useSnackbar();


    const {
        handleShow,
        handleShowApiUser,
        userList,
        toggleStatus,
        handleEditClick,
        setReload,
        // toggleDelete
    } = props;
    const userData = (userList.length > 0) ? userList : [];



    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            otp: Yup.string().required('OTP is required'),
            newMobileNumber: Yup.string().required('New mobile number is required'),
        })),
        defaultValues: {
            otp: '',
            piiAccess: false,
            unmaskedAddressAccess: false,
        }
    });

    const { watch, setValue, reset } = methods;

    const piiAccessToggle = watch('piiAccess');
    const mfaEnabledToggle = watch('mfaEnabled');
    const unmaskedAddressAccessToggle = watch('unmaskedAddressAccess');
    const mfaOtp = watch('piiOtp');

    useEffect(() => {
        if (sendOtpData) {
            if (type === 'piiAccess') {
                setValue('piiAccess', sendOtpData?.piiAccess);
            } else if (type === 'mfa') {
                setValue('mfaEnabled', sendOtpData?.mfaenabled);
            } else if (type === 'unmaskedAddressAccess') {
                setValue('unmaskedAddressAccess', sendOtpData?.unmaskedAddressAccess);
            }
        }
    }, [sendOtpData]);


    const handleShowLoginHistory = (logins) => {
        if (logins && logins.lastLogin) {
            // Combine all logins and sort by date (newest first), then take first 10
            const allLogins = [
                ...(logins.pastLogins || []),
                logins.lastLogin
            ]
                .sort((a, b) => new Date(b) - new Date(a)) // Sort descending (newest first)
                .slice(0, 10);
            setSelectedUserLogins(allLogins);
            setLoginModalOpen(true);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };


    const handleSendOtp = async () => {
        if (!sendOtpData) {
            enqueueSnackbar('User information is required to send OTP', { variant: 'error' });
            return;
        }
        setSendingOtp(true);
        try {
            const response = await postGeneric(apiConstants.MODIFY_SEND_OTP, {
                username: sendOtpData?.email || sendOtpData?.username,
                purpose: type === 'piiAccess' ? 'PII_ACCESS_EDIT' :
                    type === 'mfa' ? 'MFA_ENABLE' :
                        type === 'unmaskedAddressAccess' ? 'UNMASKED_ADDRESS_ACCESS' :
                            type === 'mobileNumber' ? 'MOBILE_NUMBER_CHANGE' : '-'
            });
            if (response.status === 200) {
                setOtpSent(true);
                setTokenId(response.data?.tokenId);
                enqueueSnackbar('OTP has been sent to your registered mobile number', { variant: 'success' });
            } else {
                enqueueSnackbar(response.data?.message || 'Failed to send OTP', { variant: 'error' });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(err.response?.data?.message || 'Failed to send OTP', { variant: 'error' });
        } finally {
            setSendingOtp(false);
        }
    };

    const newMobileNumber = watch('newMobileNumber');

    const handleVerifyOtp = async () => {
        setVerifyingOtp(true);
        try {
            const payload = {
                username: sendOtpData?.email || sendOtpData?.username,
                tokenId: tokenId,
                otp: mfaOtp,
            }
            if (type === 'piiAccess') {
                payload.piiAccess = piiAccessToggle;
            } else if (type === 'mobileNumber') {
                payload.phoneNumber = newMobileNumber;
            } else if (type === 'mfa') {
                payload.mfaEnabled = mfaEnabledToggle;
            } else if (type === 'unmaskedAddressAccess') {
                payload.unmaskedAddressAccess = unmaskedAddressAccessToggle;
            }
            // payload
            const endpoint = type === 'piiAccess' ? apiConstants.MODIFY_PII_ACCESS :
                type === 'unmaskedAddressAccess' ? apiConstants.MODIFY_UNMASKED_ADDRESS_ACCESS_UPDATE :
                    type === 'mfa' ? apiConstants.MODIFY_MFA_ENABLE :
                        type === 'mobileNumber' ? apiConstants.SEND_OTP_FOR_USER_MOBILE_NUMBER_UPDATE : '-';
            const response = await postGeneric(endpoint, payload);
            if (response.status === 200) {
                enqueueSnackbar(response.data?.message || 'Access updated successfully', { variant: 'success' });
                setReload((reload) => reload + 1);
                handleCloseOtpDialog();
            } else {
                enqueueSnackbar(response.data?.message || 'Failed to update access', { variant: 'error' });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(err.response?.data?.message || 'Failed to update access', { variant: 'error' });
        } finally {
            setVerifyingOtp(false);
        }
    }


    const handleCloseOtpDialog = () => {
        setOtpDialogOpen(false);
        setOtpSent(false);
        setSendingOtp(false);
        setVerifyingOtp(false);
        reset();
    }
    const [type, setType] = useState(null);

    const handleOpenOtpDialog = (row, type) => {
        setOtpDialogOpen(true);
        setSendOtpData(row);
        setType(type);
    }


    return (
        <>
            <MUIModal
                open={loginModalOpen}
                handleClose={() => setLoginModalOpen(false)}
                title="Login History (Last 10 Logins)"
                maxWidth="sm"
            >
                <List sx={{ py: 0 }}>
                    {selectedUserLogins && selectedUserLogins.length > 0 ? (
                        selectedUserLogins.map((login, index) => (
                            <ListItem key={index} divider={index < selectedUserLogins.length - 1} sx={{ py: 1.5, px: 2 }}>
                                <ListItemText
                                    primary={
                                        <Typography sx={{ ...primaryTextSx, fontSize: '12.5px' }}>
                                            Login {index + 1}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography sx={{ ...secondaryTextSx, mt: 0.25 }}>
                                            {formatDate(login)}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))
                    ) : (
                        <ListItem sx={{ py: 2, px: 2 }}>
                            <ListItemText primary={
                                <Typography sx={secondaryTextSx}>No login history available</Typography>
                            } />
                        </ListItem>
                    )}
                </List>
            </MUIModal>
            <Box sx={filterBarSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '14px', color: '#1B2A4A' }}>All Users</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant='contained'
                            onClick={handleShow}
                            size="small"
                            startIcon={<AddIcon />}
                            sx={primaryBtnSx}>
                            Add User
                        </Button>
                        <Button variant='outlined'
                            onClick={handleShowApiUser}
                            size="small"
                            startIcon={<ApiIcon />}
                            sx={secondaryBtnSx}
                            title="Create a user that can only call Proship APIs (e.g. from Postman) and has no UI access">
                            Create API User
                        </Button>
                    </Box>
                </Box>

                <ProzoDataGrid
                    columns={[
                        {
                            field: 'fullName',
                            headerName: 'Name',
                            flex: 1.3,
                            minWidth: 140
                        },
                        {
                            field: 'roles',
                            headerName: 'Role',
                            flex: 1,
                            minWidth: 100
                        },
                        {
                            field: 'email',
                            headerName: 'Login Email',
                            flex: 1.2,
                            minWidth: 160
                        },
                        {
                            field: 'status',
                            headerName: 'Status',
                            flex: 1,
                            minWidth: 110,
                            renderCell: (item) => {
                                const isActive = item.value === 'ACTIVE';
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size='small'
                                                    checked={isActive}
                                                    onChange={() => toggleStatus(item.id, item.value)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2e7d32' },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography sx={{ ...secondaryTextSx, color: isActive ? '#2e7d32' : '#9e9e9e', fontWeight: 600 }}>
                                                    {titleCase(item.value)}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                )
                            }
                        },
                        {
                            field: 'createdByUser',
                            headerName: 'Created',
                            flex: 1.2,
                            minWidth: 150,
                            renderCell: (item) => {
                                const createdBy = item.row?.createdByUser || '-';
                                const createdDateRaw = item.row?.createdDate;
                                let createdDateStr = '-';
                                if (createdDateRaw) {
                                    try {
                                        createdDateStr = new Date(createdDateRaw).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        });
                                    } catch (e) {
                                        createdDateStr = createdDateRaw;
                                    }
                                }
                                return (
                                    <Box sx={cellStackSx}>
                                        <Typography sx={primaryTextSx} noWrap title={createdBy}>
                                            {createdBy}
                                        </Typography>
                                        <Typography sx={secondaryTextSx} noWrap>
                                            {createdDateStr}
                                        </Typography>
                                    </Box>
                                );
                            }
                        },
                        {
                            field: 'logins',
                            headerName: 'Last Login',
                            flex: 1.4,
                            minWidth: 160,
                            renderCell: (item) => {
                                const logins = item.row?.logins;
                                const lastLogin = logins?.lastLogin;
                                return (
                                    <Box sx={cellStackSx}>
                                        <Typography sx={secondaryTextSx}>
                                            {formatDate(lastLogin)}
                                        </Typography>
                                        {logins && logins.lastLogin && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<HistoryIcon />}
                                                onClick={() => handleShowLoginHistory(logins)}
                                                sx={{ ...secondaryBtnSx, fontSize: '11px', px: 1 }}
                                            >
                                                History
                                            </Button>
                                        )}
                                    </Box>
                                )
                            }
                        },
                        {
                            field: 'piiAccess',
                            headerName: 'PII Access',
                            flex: 0.8,
                            minWidth: 90,
                            renderCell: (item) => {
                                const piiAccess = item.value === true || item.value === 'true';
                                return (
                                    <Chip
                                        label={piiAccess ? 'Yes' : 'No'}
                                        color={piiAccess ? 'success' : 'default'}
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                        variant="outlined"
                                    />
                                )
                            }
                        },

                        {
                            fieldName: 'action',
                            headerName: 'Action',
                            flex: 0.8,
                            minWidth: 60,
                            renderCell: (item) => {
                                const { row } = item;
                                const currentUserRoles = getRole();
                                const isAdmin = currentUserRoles && (currentUserRoles.includes('ROLE_ADMIN') || currentUserRoles.includes('ROLE_PROSHIPADMIN'));

                                if (!isAdmin) {
                                    return null;
                                }

                                return (
                                    <RowActionsMenu
                                        row={row}
                                        onEditClick={(id) => handleEditClick(id)}
                                        onOpenOtpDialog={(r, type) => handleOpenOtpDialog(r, type)}
                                    />
                                );
                            }
                        }
                    ]}
                    rowHeight={null}
                    getRowHeight={() => 'auto'}
                    // rows={userData?.filter(p => !((p.username).startsWith('system_user'))) || []}
                    rows={userData}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    paginationMode={'client'}
                    hideFooterPagination={false}
                    rowCount={userData?.length}
                    getRowId={(row) => row.id}
                />
            </Box>
            {/* OTP Dialog for Mobile Number Update */}
            <MUIModal
                open={otpDialogOpen}
                handleClose={handleCloseOtpDialog}
                maxWidth="sm"
                title={type === 'piiAccess' ? 'Update PII Access' :
                    type === 'mfa' ? 'Update MFA' :
                        type === 'unmaskedAddressAccess' ? 'Update Address View' :
                            type === 'mobileNumber' ? 'Update Mobile Number' : '-'}
                hideClose={false}
                action={
                    <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
                        {!otpSent ? (
                            <LoadingButton
                                variant="contained"
                                loading={sendingOtp}
                                onClick={handleSendOtp}
                            >
                                Send OTP
                            </LoadingButton>
                        ) : (
                            <LoadingButton
                                variant="contained"
                                loading={verifyingOtp}
                                onClick={handleVerifyOtp}
                                disabled={!mfaOtp || mfaOtp.length < 4}
                            >
                                Verify & Update
                            </LoadingButton>
                        )}
                    </Box>
                }
            >
                <FormProvider methods={methods}>
                    <Box sx={{ p: 2 }}>
                        <Alert
                            severity="info"
                            sx={{ mb: 3 }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Sensitive Data Change
                            </Typography>
                            <Typography variant="body2">
                                Changing {type === 'piiAccess' ? 'PII Access' :
                                    type === 'mfa' ? 'MFA' :
                                        type === 'unmaskedAddressAccess' ? 'Full Address View' :
                                            type === 'mobileNumber' ? 'Mobile Number' : '-'} is a sensitive operation. An OTP will be sent to your registered mobile number to verify your identity before proceeding with the update.
                            </Typography>
                        </Alert>

                        {!otpSent ? (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Click the "Send OTP" button below to receive a verification code on your registered mobile number.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                                    Current {type === 'piiAccess' ? 'PII Access' :
                                        type === 'mfa' ? 'MFA' :
                                            type === 'unmaskedAddressAccess' ? 'Full Address View' :
                                                type === 'mobileNumber' ? 'Mobile Number' : '-'} :
                                    <strong>{type === 'piiAccess' ? (sendOtpData?.piiAccess ? 'Enabled' : 'Disabled') :
                                        type === 'mfa' ? (sendOtpData?.mfaenabled ? 'Enabled' : 'Disabled') :
                                            type === 'unmaskedAddressAccess' ? (sendOtpData?.unmaskedAddressAccess ? 'Enabled' : 'Disabled') :
                                                type === 'mobileNumber' ? (sendOtpData?.mobile ? sendOtpData?.mobile : '-') : '-'
                                    }</strong>
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {type === 'mobileNumber'
                                        ? 'Please enter the new mobile number and the OTP sent to your registered mobile number to verify the change.'
                                        : 'Please toggle the PII Access setting below and enter the OTP sent to your registered mobile number to verify the change.'
                                    }
                                </Typography>

                                <Grid container spacing={2}>
                                    {type !== 'mobileNumber' && (
                                        <Grid item xs={12}>
                                            <Box sx={{
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                backgroundColor: 'background.paper'
                                            }}>
                                                <RHFCheckbox
                                                    name={type === 'piiAccess' ? 'piiAccess' : type === 'mfa' ? 'mfaEnabled' : type === 'unmaskedAddressAccess' ? 'unmaskedAddressAccess' : null}
                                                    label={(
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                                {type === 'piiAccess' ? 'PII Data Access' : type === 'mfa' ? 'MFA Enabled' : type === 'unmaskedAddressAccess' ? 'Full Address View' : ''}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {type === 'piiAccess' ? 'Allow access to Personally Identifiable Information' : type === 'mfa' ? 'Enable Multi-Factor Authentication' : type === 'unmaskedAddressAccess' ? 'Allow access to full address view' : ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                    {type === 'mobileNumber' && (
                                        <Grid item xs={12}>
                                            <RHFTextField
                                                name="newMobileNumber"
                                                label="New Mobile Number"
                                                placeholder="Enter 10-digit mobile number"
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <RHFTextField
                                            name="piiOtp"
                                            label="Enter OTP"
                                            placeholder="Enter 6-digit OTP"
                                            autoFocus
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </FormProvider>
            </MUIModal>

        </>
    );
};
export default UserList;
